import {
  CircularProgress,
  makeStyles,
  Popover,
  Tooltip,
} from '@material-ui/core';
import { DescriptionOutlined, FileCopyOutlined } from '@material-ui/icons';
import EditIcon from '@material-ui/icons/Edit';
import { LOAD_STATUS } from 'doc-mate-store/lib/constants/load';
import { useStores } from 'doc-mate-store/lib/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { downloadAllStopsPaperworkPdf } from '../../../utils/paperworkPdf';

type Props = {
  cell: {
    value: [
      string, // text
      string, // status
      number, // manifestId
      boolean, // isDispatcher
      boolean?, // canCopy
      boolean?, // isBilling
      (string | number)?, // loadId
      string?, //AS400 Manifest Number
    ];
  };
};

const Link: React.FC<Props> = props => {
  const classes = useStyles();
  const { rootStore } = useStores();
  const { cell } = props;
  const {
    value: [
      text,
      status,
      manifestId,
      isDispatcher,
      canCopy = true,
      isBilling = false,
      loadId,
      as400ManifestNumber,
    ], // eslint-disable-line  @typescript-eslint/no-unused-vars
  } = cell;
  const [copy, setCopy] = useState(false);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPaperwork = useCallback(
    async (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!loadId || downloading) return;

      setDownloading(true);
      try {
        const legIdNumber = Number(loadId);
        const legIdString = String(loadId);

        rootStore.orderlegs.delete(legIdString);

        await rootStore.fetchOrderLeg(legIdNumber);
        const leg = rootStore.orderlegs.get(legIdString);

        if (!leg) {
          alert('Unable to load document details for this order leg.');
          return;
        }

        // Generate and download the all-stops paperwork PDF
        const result = await downloadAllStopsPaperworkPdf(
          leg,
          as400ManifestNumber,
        );
        if (result.empty) {
          alert('No paperwork or documents available to export yet.');
        }
      } catch (err) {
        console.error('Failed to download paperwork:', err);
        alert('An error occurred while generating the paperwork PDF.');
      } finally {
        setDownloading(false);
      }
    },
    [loadId, downloading, rootStore, as400ManifestNumber],
  );
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    if (copy) {
      setTimeout(() => setCopy(false), 1000);
    }
  }, [copy]);

  const handleEdit = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.stopPropagation();
      rootStore.setUpdateManifestId(manifestId);
    },
    [rootStore, manifestId],
  );

  const handleCopy = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.stopPropagation();
      rootStore.setCopyManifestId(manifestId);
    },
    [rootStore, manifestId],
  );

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      navigator.clipboard.writeText(text);
      setAnchorEl(event.currentTarget);
      setCopy(true);
    },
    [text],
  );

  return (
    <>
      <div className={classes.root}>
        <span className={classes.link} onClick={handleClick}>
          {text}
        </span>
        <span className={classes.iconContainer}>
          {/* Billing Paperwork Download Action */}
          {isBilling && (
            <span
              className={classes.icon}
              onClick={handleDownloadPaperwork}
              role="button"
              tabIndex={0}
            >
              <Tooltip title="Download Paperwork">
                {downloading ? (
                  <CircularProgress size={14} color="primary" />
                ) : (
                  <DescriptionOutlined
                    aria-label="Download Paperwork"
                    color="primary"
                    fontSize="inherit"
                  />
                )}
              </Tooltip>
            </span>
          )}

          {/* Dispatcher Actions */}
          {isDispatcher && (
            <>
              {status !== LOAD_STATUS.INVOICED && (
                <span className={classes.icon} onClick={handleEdit}>
                  <Tooltip title="Edit Manifest">
                    <EditIcon
                      aria-label="Edit Manifest"
                      color="primary"
                      fontSize="inherit"
                    />
                  </Tooltip>
                </span>
              )}
              {canCopy && (
                <span className={classes.icon} onClick={handleCopy}>
                  <Tooltip title="Copy Manifest">
                    <FileCopyOutlined
                      aria-label="Copy Manifest"
                      color="primary"
                      fontSize="inherit"
                    />
                  </Tooltip>
                </span>
              )}
            </>
          )}
        </span>
      </div>
      <Popover
        id={text}
        open={copy}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <div className={classes.popUpText}>Copied</div>
      </Popover>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    flexDirection: 'row',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
  link: {
    overflow: 'hidden',
    paddingRight: theme.spacing(0.25),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
  iconContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: 1,
  },
  icon: {
    display: 'inline-flex',
    alignItems: 'center',
    paddingRight: theme.spacing(1),
    cursor: 'pointer',
  },
  popUpText: {
    fontSize: 12,
    padding: 5,
  },
}));

export default React.memo(Link);
